import React, { useEffect, useState } from 'react';
import { Landmark, Sparkles } from 'lucide-react';
import {
  canAccessFeature,
  hasAcademyManagementRole,
  TierFeatureKey,
} from '@court-sync/shared';
import { useTour, TourAlertDialog } from '@/components/ui/Tour';
import { getClubManagementTourSteps } from '@/lib/club-management-tour-steps';
import { getEnterpriseUpgradeTourSteps } from '@/lib/enterprise-upgrade-tour-steps';
import { getProfessionalUpgradeTourSteps } from '@/lib/professional-upgrade-tour-steps';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useMatchPlayAccess } from '@/hooks/useMatchPlayAccess';
import { useSubscription } from '@/hooks/useSubscription';
import { useClubManagementTourStore } from '@/store/clubManagementTour';
import { useEnterpriseUpgradeTourStore } from '@/store/enterpriseUpgradeTour';
import { useProfessionalUpgradeTourStore } from '@/store/professionalUpgradeTour';
import { useAuthStore } from '@/store/auth';
import type { UpsellTourKind } from '@/lib/tours/tourSession';

interface ClubManagementTourHostInnerProps {
  children: React.ReactNode;
  activeTourKind: UpsellTourKind | null;
  onActiveTourKindChange: (kind: UpsellTourKind | null) => void;
}

function ClubManagementTourHostInner({
  children,
  activeTourKind,
  onActiveTourKindChange,
}: ClubManagementTourHostInnerProps) {
  const { user } = useAuthStore();
  const { startWithSteps, close, isActive } = useTour();
  const { beginUpsellTourSession } = usePageTourActions();
  const { hasClubManagement, isLoading: clubEntitlementsLoading } = useClubModuleAccess();
  const { hasAccess: hasMatchPlay, isLoading: matchPlayLoading } = useMatchPlayAccess();
  const { currentTier, isLoading: subscriptionLoading } = useSubscription();
  const clubPending = useClubManagementTourStore((s) => s.pending);
  const clearClubPending = useClubManagementTourStore((s) => s.clearPending);
  const professionalPending = useProfessionalUpgradeTourStore((s) => s.pending);
  const clearProfessionalPending = useProfessionalUpgradeTourStore((s) => s.clearPending);
  const enterprisePending = useEnterpriseUpgradeTourStore((s) => s.pending);
  const clearEnterprisePending = useEnterpriseUpgradeTourStore((s) => s.clearPending);
  const [showDialog, setShowDialog] = useState(false);

  const hasAcademyManagement = Boolean(user && hasAcademyManagementRole(user));
  const hasProfessionalFeatures = canAccessFeature(currentTier, TierFeatureKey.STUDENT_GUARDIAN_PORTAL);
  const hasEnterpriseFeatures = canAccessFeature(
    currentTier,
    TierFeatureKey.ACCOUNTING_INTEGRATIONS,
  );
  const professionalTourCompleted = user?.preferences?.tourCompletedProfessionalUpgrade ?? false;
  const enterpriseTourCompleted = user?.preferences?.tourCompletedEnterpriseUpgrade ?? false;
  const clubTourCompleted = user?.preferences?.tourCompletedClubManagement ?? false;
  const dashboardTourCompleted = hasAcademyManagement
    ? (user?.preferences?.tourCompletedAcademyOwnerDashboard ?? false)
    : (user?.preferences?.tourCompletedClubOwnerDashboard ?? false);

  const resolveUpsellTourSteps = React.useCallback(
    (kind: UpsellTourKind) => {
      if (kind === 'professional') {
        return getProfessionalUpgradeTourSteps({ hasMatchPlay: hasMatchPlay && !matchPlayLoading });
      }
      if (kind === 'enterprise') {
        return getEnterpriseUpgradeTourSteps();
      }
      return getClubManagementTourSteps({ hasAcademyManagement });
    },
    [hasAcademyManagement, hasMatchPlay, matchPlayLoading],
  );

  useEffect(() => {
    if (!isActive && activeTourKind) {
      onActiveTourKindChange(null);
    }
  }, [activeTourKind, isActive, onActiveTourKindChange]);

  useEffect(() => {
    if (!user || activeTourKind || isActive || !dashboardTourCompleted) return undefined;

    const canShowProfessionalTour =
      professionalPending &&
      !subscriptionLoading &&
      hasProfessionalFeatures &&
      hasAcademyManagement &&
      !professionalTourCompleted;

    const canShowEnterpriseTour =
      enterprisePending &&
      !subscriptionLoading &&
      hasEnterpriseFeatures &&
      hasAcademyManagement &&
      !enterpriseTourCompleted;

    const canShowClubTour =
      clubPending &&
      !clubEntitlementsLoading &&
      hasClubManagement &&
      !clubTourCompleted;

    const nextTourKind: UpsellTourKind | null = canShowProfessionalTour
      ? 'professional'
      : canShowEnterpriseTour
        ? 'enterprise'
        : canShowClubTour
          ? 'club'
          : null;

    if (!nextTourKind) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onActiveTourKindChange(nextTourKind);
      setShowDialog(true);
      if (nextTourKind === 'professional') {
        clearProfessionalPending();
      } else if (nextTourKind === 'enterprise') {
        clearEnterprisePending();
      } else {
        clearClubPending();
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    user,
    activeTourKind,
    isActive,
    dashboardTourCompleted,
    professionalPending,
    enterprisePending,
    clubPending,
    subscriptionLoading,
    hasProfessionalFeatures,
    hasEnterpriseFeatures,
    hasAcademyManagement,
    professionalTourCompleted,
    enterpriseTourCompleted,
    clubEntitlementsLoading,
    hasClubManagement,
    clubTourCompleted,
    clearProfessionalPending,
    clearEnterprisePending,
    clearClubPending,
    onActiveTourKindChange,
  ]);

  const dialogProps =
    activeTourKind === 'professional'
      ? {
          icon: Sparkles,
          title: 'Welcome to Professional',
          description:
            'Your plan now includes member portals, online payments, and automation tools. Take a quick tour of what just unlocked.',
          confirmLabel: 'Show new features',
        }
      : activeTourKind === 'enterprise'
        ? {
            icon: Sparkles,
            title: 'Welcome to Enterprise',
            description:
              'Accounting integrations, unlimited administrators, and a higher SMS allowance are now included. Take a quick tour of what unlocked.',
            confirmLabel: 'Show new features',
          }
        : {
            icon: Landmark,
            title: hasAcademyManagement ? 'Club management tour' : 'Welcome to club management',
            description: hasAcademyManagement
              ? 'Your sidebar includes dedicated Club and Organization areas. Take a quick tour of club navigation.'
              : 'Take a quick tour of the facility and club tools in your sidebar — court hire, members, payments, and more.',
            confirmLabel: 'Show me around',
          };

  return (
    <>
      {children}
      <TourAlertDialog
        isOpen={showDialog}
        setIsOpen={(open) => {
          setShowDialog(open);
          if (!open) {
            onActiveTourKindChange(null);
          }
        }}
        icon={dialogProps.icon}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmLabel={dialogProps.confirmLabel}
        cancelLabel="Maybe later"
        onConfirm={() => {
          if (!activeTourKind) return;
          const steps = resolveUpsellTourSteps(activeTourKind);
          if (steps.length === 0) return;
          beginUpsellTourSession(activeTourKind);
          startWithSteps(steps);
        }}
        onSkip={() => {
          if (activeTourKind) {
            beginUpsellTourSession(activeTourKind);
            close();
          }
          onActiveTourKindChange(null);
        }}
      />
    </>
  );
}

export function ClubManagementTourHost({ children }: { children: React.ReactNode }) {
  const [activeTourKind, setActiveTourKind] = useState<UpsellTourKind | null>(null);

  return (
    <ClubManagementTourHostInner activeTourKind={activeTourKind} onActiveTourKindChange={setActiveTourKind}>
      {children}
    </ClubManagementTourHostInner>
  );
}
